import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  ArrowUpCircle,
  Cpu,
} from "lucide-react";

interface AgentLatest {
  version: string;
  sha: string;
  timestamp: string;
}

interface Device {
  deviceId: string;
  friendlyName?: string;
  lastSeen?: string;
  shadow?: {
    state?: {
      reported?: {
        agent_version?: string;
      };
    };
  };
}

export function AgentPage() {
  const [latest, setLatest] = useState<AgentLatest | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<{ deployId: string; deviceCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get("/agent/latest").then(({ data }) => setLatest(data)).catch(() => {}),
      api.get("/devices").then(({ data }) => setDevices(data.devices ?? [])).catch(() => {}),
    ]).then(() => setLoading(false));
  }, []);

  const getDeviceVersion = (d: Device): string =>
    d.shadow?.state?.reported?.agent_version || "unknown";

  const isOutdated = (d: Device): boolean => {
    if (!latest) return false;
    const v = getDeviceVersion(d);
    return v !== "unknown" && v !== latest.version;
  };

  const outdatedDevices = devices.filter(isOutdated);
  const uptodateDevices = devices.filter((d) => !isOutdated(d) && getDeviceVersion(d) !== "unknown");
  const unknownDevices = devices.filter((d) => getDeviceVersion(d) === "unknown");

  const handleUpdateAll = async () => {
    setUpdating(true);
    setError(null);
    setUpdateResult(null);
    try {
      const { data } = await api.post("/agent/update", {
        deviceGroup: "pistack-fleet",
      });
      setUpdateResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateSelected = async (deviceIds: string[]) => {
    setUpdating(true);
    setError(null);
    setUpdateResult(null);
    try {
      const { data } = await api.post("/agent/update", { deviceIds });
      setUpdateResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="px-8 pt-8 pb-12 max-w-4xl">
      <h1 className="text-2xl font-semibold text-surface-900">Agent Management</h1>
      <p className="mt-1 text-sm text-surface-500">
        Manage the PiStack agent version running on your fleet devices.
      </p>

      {/* Latest version card */}
      <div className="mt-6 bg-surface-0 rounded-xl border border-surface-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold text-surface-800">Latest Available Version</h2>
            {latest ? (
              <div className="mt-2">
                <span className="text-2xl font-bold text-surface-900 font-mono">
                  v{latest.version}
                </span>
                <span className="ml-3 text-xs text-surface-400 font-mono">
                  {latest.sha.slice(0, 8)}
                </span>
                <p className="mt-1 text-xs text-surface-400">
                  Published {new Date(latest.timestamp).toLocaleString()}
                </p>
              </div>
            ) : loading ? (
              <p className="mt-2 text-sm text-surface-400">Loading...</p>
            ) : (
              <p className="mt-2 text-sm text-warning-500">
                No agent version published. Run the Deploy Agent workflow.
              </p>
            )}
          </div>
          {latest && outdatedDevices.length > 0 && (
            <button
              onClick={handleUpdateAll}
              disabled={updating}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-surface-950 text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors cursor-pointer disabled:opacity-50"
            >
              <ArrowUpCircle size={16} />
              {updating ? "Updating..." : `Update All (${outdatedDevices.length})`}
            </button>
          )}
        </div>
      </div>

      {/* Update result */}
      {updateResult && (
        <div className="mt-4 p-4 rounded-lg bg-success-100 border border-success-500/20">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-success-500" />
            <span className="text-sm font-medium text-surface-800">
              Update triggered for {updateResult.deviceCount} device{updateResult.deviceCount !== 1 ? "s" : ""}
            </span>
          </div>
          <p className="mt-1 text-xs text-surface-500 font-mono">
            Deploy ID: {updateResult.deployId}
          </p>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 rounded-lg bg-danger-100 border border-danger-500/20 text-sm text-danger-500">
          {error}
        </div>
      )}

      {/* Fleet version overview */}
      {!loading && devices.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-surface-800 mb-3">Fleet Status</h2>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-surface-0 rounded-xl border border-surface-200 p-4 text-center">
              <div className="text-2xl font-bold text-success-500">{uptodateDevices.length}</div>
              <div className="text-xs text-surface-400 mt-1">Up to date</div>
            </div>
            <div className="bg-surface-0 rounded-xl border border-surface-200 p-4 text-center">
              <div className="text-2xl font-bold text-warning-500">{outdatedDevices.length}</div>
              <div className="text-xs text-surface-400 mt-1">Outdated</div>
            </div>
            <div className="bg-surface-0 rounded-xl border border-surface-200 p-4 text-center">
              <div className="text-2xl font-bold text-surface-400">{unknownDevices.length}</div>
              <div className="text-xs text-surface-400 mt-1">Unknown</div>
            </div>
          </div>

          {/* Device list */}
          <div className="bg-surface-0 rounded-xl border border-surface-200 divide-y divide-surface-200">
            {devices.map((device) => {
              const version = getDeviceVersion(device);
              const outdated = isOutdated(device);

              return (
                <div key={device.deviceId} className="px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                      <Cpu size={16} className="text-brand-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-surface-900">
                        {device.friendlyName || device.deviceId}
                      </div>
                      {device.friendlyName && (
                        <div className="text-[11px] text-surface-400 font-mono">{device.deviceId}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className={`text-sm font-mono ${outdated ? "text-warning-500" : "text-surface-700"}`}>
                        v{version}
                      </div>
                      {outdated && latest && (
                        <div className="text-[11px] text-surface-400">
                          latest: v{latest.version}
                        </div>
                      )}
                    </div>

                    {outdated ? (
                      <button
                        onClick={() => handleUpdateSelected([device.deviceId])}
                        disabled={updating}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-warning-500 border border-warning-500/30 rounded-lg hover:bg-warning-100 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        <ArrowUpCircle size={14} />
                        Update
                      </button>
                    ) : version !== "unknown" ? (
                      <span className="flex items-center gap-1.5 text-xs text-success-500">
                        <CheckCircle2 size={14} />
                        Current
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-xs text-surface-400">
                        <AlertTriangle size={14} />
                        Unknown
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && devices.length === 0 && (
        <div className="mt-8 text-center py-16 bg-surface-0 rounded-xl border border-surface-200">
          <RefreshCw size={40} className="mx-auto text-surface-400 mb-4" strokeWidth={1.2} />
          <h2 className="text-lg font-medium text-surface-800">No devices in fleet</h2>
          <p className="mt-2 text-sm text-surface-500 max-w-sm mx-auto">
            Register and provision devices first, then manage agent updates here.
          </p>
        </div>
      )}

      {/* How versioning works */}
      <div className="mt-8 p-5 rounded-xl bg-surface-0 border border-surface-200">
        <h3 className="text-sm font-semibold text-surface-800 mb-3">How Agent Versioning Works</h3>
        <div className="space-y-3 text-sm text-surface-500">
          <div>
            <span className="font-medium text-surface-700">Version source: </span>
            The agent version comes from <code className="text-brand-400 font-mono text-xs">agent/package.json</code> in
            the repo. Bump the version there before running the Deploy Agent workflow.
          </div>
          <div>
            <span className="font-medium text-surface-700">Publishing: </span>
            The Deploy Agent GitHub Actions workflow builds the agent, packages it as a tarball, and uploads it
            to S3 with both a semver tag and a "latest" pointer.
          </div>
          <div>
            <span className="font-medium text-surface-700">Updating: </span>
            Click "Update" to push the latest agent version to selected devices (or the entire fleet).
            The agent downloads the new tarball, validates it, backs up the current version, swaps in the
            new one, and restarts itself.
          </div>
          <div>
            <span className="font-medium text-surface-700">Rollback: </span>
            The agent keeps the previous version at <code className="text-brand-400 font-mono text-xs">/opt/pistack/agent.prev/</code>.
            If the update fails, the agent automatically restores from this backup.
          </div>
        </div>
      </div>
    </div>
  );
}
